import type { UploadMethod } from "@/analytics/surfaces";
import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import {
  generateUniqueName,
  useFileConflictResolver,
} from "@/components/FileConflictResolver";
import { convertHeicToJpegIfNeeded } from "@/utils/imageFilePrep";
import {
  AttachedFile,
  FileRecord,
  ListFileRecordsParams,
} from "@cohesive/api-client";
import { QueryClient } from "@tanstack/react-query";
import mime from "mime-types";
import { useCallback, useMemo } from "react";
import { useFileRecordUploadQueue } from "./useFileRecordUploadQueue";

export const allowedMimeTypes = {
  // Plain text
  eml: "message/rfc822",
  html: "text/html",
  json: "application/json",
  md: "text/markdown",
  msg: "application/vnd.ms-outlook",
  rst: "text/x-rst",
  rtf: "application/rtf",
  txt: "text/plain",
  xml: "application/xml",
  // Images
  png: "image/png",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  tiff: "image/tiff",
  bmp: "image/bmp",
  heic: "image/heic",
  // Documents
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  epub: "application/epub+zip",
  odt: "application/vnd.oasis.opendocument.text",
  pdf: "application/pdf",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  tsv: "text/tab-separated-values",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const;

export const dropzoneAccept: { [key: string]: string[] } = Object.entries(
  allowedMimeTypes,
).reduce(
  (acc, [ext, mimeType]) => {
    if (!acc[mimeType]) {
      acc[mimeType] = [];
    }
    acc[mimeType] = acc[mimeType] || [];
    // @ts-ignore
    acc[mimeType].push(`.${ext}`);
    return acc;
  },
  {} as { [key: string]: string[] },
);

export const allowedExtensions = Object.keys(allowedMimeTypes);

/**
 * Where a freshly-minted file_record should land. Discriminator for both
 * the destination passed to `POST /file-records` and the existing-names
 * lookup that drives client-side rename.
 */
export type UploadTarget =
  | { class: "flow-session"; flow_session_id: string }
  | { class: "canvas"; canvas_id: string }
  | { class: "workspace"; workspace_id: string; parentPath?: string };

const getImageDimensions = async (
  file: File,
): Promise<{ width: number; height: number }> => {
  const imageUrl = URL.createObjectURL(file);
  const img = new Image();

  try {
    const dimensions = await new Promise<{ width: number; height: number }>(
      (resolve, reject) => {
        img.onload = () => {
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        };
        img.onerror = reject;
        img.src = imageUrl;
      },
    );
    return dimensions;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

/**
 * Compose the `destination` payload for `orgAPI.fileRecords.create` from a
 * target + leaf name. Canvas paths are bare leaf names (matching the
 * `image_generate` / `video_generate` server-side convention); flow-session
 * paths sit under `/attachments/` to keep chat uploads grouped; workspace
 * paths preserve the caller-supplied parent directory.
 */
export function buildDestinationForTarget(
  target: UploadTarget,
  uniqueName: string,
) {
  if (target.class === "canvas") {
    return {
      class: "canvas" as const,
      canvas_id: target.canvas_id,
      path: uniqueName,
      is_folder: false,
    };
  }
  if (target.class === "workspace") {
    return {
      class: "workspace" as const,
      workspace_id: target.workspace_id,
      path: `${target.parentPath ?? ""}${uniqueName}`,
      is_folder: false,
    };
  }
  return {
    class: "flow-session" as const,
    flow_session_id: target.flow_session_id,
    // No leading slash — server's `NormalizePath` strips them and the
    // path validator rejects them. The resolver's collision check
    // compares against stored paths verbatim, so emitting the
    // normalized form here keeps both sides aligned.
    path: `attachments/${uniqueName}`,
    is_folder: false,
  };
}

/**
 * List params for `orgAPI.fileRecords.list` derived from the target. Used
 * to build the `taken` set for client-side rename collision avoidance.
 */
export function buildListParamsForTarget(
  target: UploadTarget,
): ListFileRecordsParams {
  if (target.class === "canvas") {
    return { class: "canvas", canvas_id: target.canvas_id };
  }
  if (target.class === "workspace") {
    return { class: "workspace", workspace_id: target.workspace_id };
  }
  return { class: "flow-session", flow_session_id: target.flow_session_id };
}

/**
 * Build the destination payload when the caller has already resolved a
 * full path (e.g. through the conflict resolver). For canvas / chat the
 * resolver passes a leaf name; for workspace it passes the full path
 * including the parent directory.
 */
function buildDestinationFromResolvedPath(
  target: UploadTarget,
  resolvedPath: string,
) {
  if (target.class === "canvas") {
    return {
      class: "canvas" as const,
      canvas_id: target.canvas_id,
      path: resolvedPath,
      is_folder: false,
    };
  }
  if (target.class === "workspace") {
    return {
      class: "workspace" as const,
      workspace_id: target.workspace_id,
      path: resolvedPath,
      is_folder: false,
    };
  }
  // Strip leading slashes the resolver may have emitted (legacy callers)
  // and ensure the `attachments/` prefix is present exactly once.
  const trimmed = resolvedPath.replace(/^\/+/, "");
  return {
    class: "flow-session" as const,
    flow_session_id: target.flow_session_id,
    path: trimmed.startsWith("attachments/")
      ? trimmed
      : `attachments/${trimmed}`,
    is_folder: false,
  };
}

/** Leaf name (last `/`-segment) of a resolved path. */
function leafFromResolvedPath(path: string): string {
  return path.split("/").filter(Boolean).pop() ?? path;
}

/**
 * Pull the leaf-name set of existing file_records in the target scope so
 * `generateUniqueName` can avoid 409s on duplicate paths. A failed list
 * surfaces as an empty set — the server's unique index catches residual
 * races as a fallback.
 */
export async function listExistingNamesForTarget(
  target: UploadTarget,
): Promise<Set<string>> {
  const taken = new Set<string>();
  try {
    const existing = await orgAPI.fileRecords.list(
      buildListParamsForTarget(target),
    );
    for (const rec of existing) {
      if (rec.status === "deleted") continue;
      const leaf = rec.path.split("/").pop();
      if (leaf) taken.add(leaf);
    }
  } catch {
    // Intentional fall-through — server unique index catches the race.
  }
  return taken;
}

export interface PerformUploadArgs {
  file: File;
  target: UploadTarget;
  /**
   * When true the server upserts at the destination path, preserving the
   * existing record's id / share state and updating only its content
   * fields. Wired to the conflict resolver's "Replace contents" branch.
   */
  overwrite?: boolean;
  /**
   * Resolved destination leaf (or full path for workspace) — bypasses the
   * primitive's collision-rename pass when the caller has already settled
   * on a target name (e.g. via the conflict resolver). Falls back to
   * `generateUniqueName` against `takenNames`/server when not set.
   */
  resolvedPath?: string;
  /**
   * Pre-built taken-names set. The queue path passes its shared in-memory
   * set so parallel uploads in the same call see each other's claims; the
   * single-shot path can omit and let the helper build a fresh set from
   * the server.
   */
  takenNames?: Set<string>;
  signal?: AbortSignal;
  queryClient: QueryClient;
  /**
   * Fired once `POST /file-records` returns the row, before the S3 POST
   * starts. Lets the queue path mirror the new id into its tile state
   * before bytes finish landing.
   */
  onRecordCreated?: (record: FileRecord) => void;
  /**
   * Fired immediately after the S3 POST resolves so callers can flip
   * UX state (e.g. queue's `s3Posted`).
   */
  onUploaded?: (record: FileRecord) => void;
}

/**
 * Single-file upload pipeline used by every Pattern A surface. Encapsulates:
 * HEIC byte conversion, unique-name resolution (when no `resolvedPath` is
 * set), `POST /file-records` (presign), S3 POST, per-id query cache pre-seed.
 *
 * Always awaits the S3 POST — throws on failure (including abort). The
 * canvas surface gets its low-latency UX from a client-local ghost layer,
 * not from a detached S3 path.
 */
export async function performFileRecordUpload({
  file: rawFile,
  target,
  overwrite,
  resolvedPath,
  takenNames,
  signal,
  queryClient,
  onRecordCreated,
  onUploaded,
}: PerformUploadArgs): Promise<{
  record: FileRecord;
  uniqueName: string;
  width?: number;
  height?: number;
  effectiveFile: File;
  effectiveType: string;
}> {
  const fileType = rawFile.type || mime.lookup(rawFile.name) || "";

  // HEIC / HEIF byte conversion. Surfaces that go through
  // `FileConflictResolverProvider.resolveUploads` have already planned the
  // final `.jpg` path, but they intentionally still pass the original File so
  // ghosts/chips can render before HEIC decode runs. Surfaces that bypass the
  // resolver still rely on this primitive for both conversion and naming.
  //
  // Run the existing-names list in parallel — `takenNames` short-
  // circuits when supplied, `resolvedPath` skips it entirely (caller
  // already settled on a destination).
  const [prepared, taken] = await Promise.all([
    convertHeicToJpegIfNeeded(rawFile),
    resolvedPath
      ? Promise.resolve(new Set<string>())
      : takenNames
        ? Promise.resolve(takenNames)
        : listExistingNamesForTarget(target),
  ]);
  const effectiveFile = prepared;
  const effectiveName = prepared === rawFile ? rawFile.name : prepared.name;
  const effectiveType = prepared === rawFile ? fileType : prepared.type;

  // Synchronous critical section: pick a unique leaf name and claim it.
  // JS is single-threaded, so from `generateUniqueName` through `taken.add`
  // no other branch can run — parallel uploads of the same basename see
  // each other's claims and resolve to distinct suffixes. Skipped when the
  // caller passed `resolvedPath` (resolver already disambiguated).
  const uniqueName = resolvedPath
    ? leafFromResolvedPath(resolvedPath)
    : generateUniqueName(effectiveName, false, taken);
  if (!resolvedPath) taken.add(uniqueName);

  // `POST /file-records` only needs `size` from the file, not the natural
  // image dimensions. Kick the dimension read off in parallel with the
  // create roundtrip and await it after.
  const dimsPromise = effectiveType.startsWith("image")
    ? getImageDimensions(effectiveFile).catch(() => undefined)
    : Promise.resolve(undefined);

  const destination = resolvedPath
    ? buildDestinationFromResolvedPath(target, resolvedPath)
    : buildDestinationForTarget(target, uniqueName);

  const record = await orgAPI.fileRecords.create({
    provider: "s3",
    size: effectiveFile.size,
    ...(overwrite ? { overwrite: true } : {}),
    destination,
  });

  // Pre-seed per-id cache so `useFileRecordsByIds` and other consumers
  // have data the moment the id is exposed — avoids a refetch race
  // before the SSE `file_record.created` event arrives on another tab.
  queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
  // When `prev` is undefined the cache hasn't been populated yet — we
  // still seed a single-element list so the just-created record shows
  // up immediately. Returning `prev` (i.e. `undefined`) would leave
  // the cache untouched and silently drop the optimistic insert,
  // forcing a full refetch before the upload appears in the UI.
  if (target.class === "canvas") {
    queryClient.setQueryData<FileRecord[]>(
      fileRecordKeys.canvas(target.canvas_id),
      (prev) => {
        if (!prev) return [record];
        const idx = prev.findIndex((r) => r.id === record.id);
        if (idx === -1) return [...prev, record];
        const next = prev.slice();
        next[idx] = { ...prev[idx], ...record };
        return next;
      },
    );
  }

  // Patch the scope's list cache so the just-created record appears
  // in the grid / explorer immediately, without waiting for the SSE
  // round-trip. Folder uploads especially benefit — the dropped tree
  // fills in record-by-record instead of all at once on the next
  // refetch.
  if (target.class === "workspace") {
    queryClient.setQueryData<FileRecord[]>(
      fileRecordKeys.workspace(target.workspace_id),
      (prev) => {
        if (!prev) return [record];
        const idx = prev.findIndex((r) => r.id === record.id);
        if (idx === -1) return [...prev, record];
        const next = prev.slice();
        next[idx] = { ...prev[idx], ...record };
        return next;
      },
    );
  }

  // Home Recents ("fileRecords","recent") is cross-class and ordered
  // server-side — the class-scoped patches above don't reach it. Mark it
  // stale so a mounted home browser refetches; the SSE `file_record.created`
  // event does the same for other tabs.
  queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });

  onRecordCreated?.(record);

  const dims = await dimsPromise;
  const width = dims?.width;
  const height = dims?.height;

  // S3 presigned POST: policy fields first, `file` last. The multipart
  // boundary is set by the browser; overriding the Content-Type header
  // makes S3 reject with 403.
  if (record.upload_url) {
    const contentType =
      effectiveFile.type || effectiveType || "application/octet-stream";
    const form = new FormData();
    for (const [k, v] of Object.entries(record.upload_field_values ?? {})) {
      form.append(k, v);
    }
    form.append("Content-Type", contentType);
    form.append("file", effectiveFile);

    const res = await fetch(record.upload_url, {
      method: "POST",
      body: form,
      signal,
    });
    if (!res.ok) {
      throw new Error(`Upload failed (${res.status})`);
    }
  }
  onUploaded?.(record);

  return { record, uniqueName, width, height, effectiveFile, effectiveType };
}

/**
 * Chat-side upload adapter. Thin wrapper around `useFileRecordUploadQueue`
 * that:
 *
 * - Routes the dropped files through the conflict resolver before
 *   enqueueing (silent rename / replace / skip per the dialog's batch).
 * - Projects queue state into the legacy `AttachedFile[]` shape that
 *   `MessageInputFilePreview` and the send-gate already consume.
 * - Derives `readyFileRecordIds` for the send-gate (only files the server
 *   has accepted are eligible to ride on the outgoing message).
 *
 * Public hook surface unchanged: callers (`useMessageActions`,
 * `MessageInput`) keep working without modification.
 */
export const useFileRecordUpload = () => {
  const { resolveUploads } = useFileConflictResolver();
  const queue = useFileRecordUploadQueue();

  /**
   * Flatten queue state into the legacy `AttachedFile[]` shape so
   * `MessageInputFilePreview` and `MessageInput`'s send-gate
   * (`pending` / `initializing` only) keep working. `id` collapses
   * `fileRecordId` (real) and `tempId` (client-only React key) into the
   * single slot AttachedFile exposes — consumers that need the real
   * file_record id should read `readyFileRecordIds` instead.
   */
  const uploadedFiles = useMemo<AttachedFile[]>(
    () =>
      queue.queue.map((q) => {
        let status: string;
        if (q.status === "failed") {
          status = "failed";
        } else if (q.status === "initializing") {
          status = "initializing";
        } else if (!q.s3Posted || q.status === "pending") {
          // The server flips `pending → processing` on the S3 object-created
          // event, after the browser's POST returns. Until then a send is
          // rejected as an invalid attachment.
          status = "pending";
        } else if (q.status === "processed") {
          status = "processed";
        } else {
          status = "processing";
        }
        return {
          id: q.fileRecordId ?? q.tempId,
          name: q.name,
          content_type: q.content_type,
          size: q.size,
          status,
          file: q.file,
          width: q.width,
          height: q.height,
          // Attached existing records preview from the server (their local
          // File is a zero-byte placeholder); uploads blob-URL their bytes.
          url: q.previewUrl,
        };
      }),
    [queue.queue],
  );

  /**
   * Ids of file records ready to ride on the outgoing message: the server
   * has accepted the object (`processing` / `processed`). Send stays
   * disabled only while uploads are `pending` / `initializing`.
   */
  const readyFileRecordIds = useMemo(
    () =>
      queue.queue
        .filter(
          (q) =>
            q.fileRecordId &&
            (q.status === "processing" || q.status === "processed"),
        )
        .map((q) => q.fileRecordId!),
    [queue.queue],
  );

  const enqueueFiles = useCallback(
    async (
      files: File[],
      target: UploadTarget,
      options?: {
        /**
         * Fires once per file as soon as `POST /file-records` returns the
         * row, before the S3 POST. Used by canvas-scoped chat to place a
         * Loro node for each uploaded file with minimal latency — the
         * caller doesn't have to wait for `processed` status to react.
         */
        onRecordCreated?: (record: FileRecord) => void;
        method?: UploadMethod;
      },
    ) => {
      if (files.length === 0) return;

      // Run the conflict resolver against the destination scope. Returns
      // `null` if the user cancelled the batch (drop nothing); otherwise
      // a list of resolved ops with renamed paths and replace/create
      // kinds baked in.
      const scope = uploadTargetToScope(target);
      const resolved = await resolveUploads({
        uploads: files.map((file) => ({
          file,
          destinationParentPath: defaultParentPathForTarget(target),
        })),
        scope,
      });
      if (resolved === null) return;

      await queue.enqueue(
        resolved.map((op) => ({
          file: op.file,
          resolvedPath: op.path,
          displayName: op.name,
          contentType: op.contentType,
          kind: op.kind === "replace" ? "replace" : "create",
        })),
        target,
        {
          method: options?.method ?? "composer",
          ...(options?.onRecordCreated
            ? { onRecordCreated: (rec) => options.onRecordCreated?.(rec) }
            : {}),
        },
      );
    },
    [queue, resolveUploads],
  );

  /**
   * Drop a queue entry by either `fileRecordId` or `tempId` — the legacy
   * `AttachedFile.id` slot collapses both, so we look up either field.
   */
  const removeFile = useCallback(
    (file: AttachedFile) => {
      const item = queue.queue.find(
        (q) => q.tempId === file.id || q.fileRecordId === file.id,
      );
      if (item) queue.removeItem(item.tempId);
    },
    [queue],
  );

  const enqueueExistingRecords = useCallback(
    (records: FileRecord[]) => {
      for (const record of records) {
        queue.enqueueExistingRecord(record);
      }
    },
    [queue],
  );

  return {
    uploadedFiles,
    readyFileRecordIds,
    enqueueFiles,
    enqueueExistingRecords,
    removeFile,
    clearFiles: queue.clear,
  };
};

/** Map an `UploadTarget` to the conflict resolver's scope discriminator. */
function uploadTargetToScope(
  target: UploadTarget,
): import("@/components/FileConflictResolver").UploadConflictScope {
  if (target.class === "workspace") {
    return { class: "workspace", workspace_id: target.workspace_id };
  }
  if (target.class === "canvas") {
    return { class: "canvas", canvas_id: target.canvas_id };
  }
  return { class: "flow-session", flow_session_id: target.flow_session_id };
}

/**
 * Where the resolver is told to compare incoming filenames against. Chat
 * always lands at `attachments/`, canvas at the root, workspace at the
 * caller-supplied parent path (the queue passes that through to the
 * destination payload).
 *
 * Paths are emitted *without* leading slashes to match the server's
 * `NormalizePath` form — the resolver's collision check lists records
 * by their stored `path` and we want byte-for-byte alignment so the
 * dialog actually surfaces collisions instead of silently producing
 * 409s on `POST /file-records`.
 */
function defaultParentPathForTarget(target: UploadTarget): string {
  if (target.class === "workspace") return target.parentPath ?? "";
  if (target.class === "flow-session") return "attachments/";
  return "";
}
